<script setup lang="ts">
  import { UiForm, UiInputFile, UiInputString } from '@uplatform-ai/fe-form-kit'

  import { getFeedbackCategoriesFeedbackCategoriesGet } from '@/shared/api/intranet'
  import { ATTACHMENTS_MAX_COUNT } from '@/shared/lib'
  import { queryKeys } from '@/shared/lib/queryKeys'
  import { ConfirmCancelModal, PageDecorationImage } from '@/shared/ui/components'

  import { SHOP_SUGGEST_SUBJECT_LENGTH, SHOP_SUGGEST_TEXT_LENGTH } from '@/entities/shop'

  import { useFeedbackForm } from '@/features/feedback'

  import { ShopSuggestMerchPageHeader } from '@/views/shop/add-merch'

  definePage({
    meta: { layout: 'baseNew' },
  })

  const { t } = useI18n()
  useHead({
    title: () => t('shop-suggest_heading'),
  })

  const DEFAULT_FORM_STATE = {
    subject: '',
    email: '',
    text: '',
    attachments: [] as File[],
  }

  const { data: feedbackCategoriesData } = useQuery({
    queryKey: queryKeys.getFeedbackCategories(),
    queryFn: () => getFeedbackCategoriesFeedbackCategoriesGet(),
  })

  const feedbackCategories = computed(() => feedbackCategoriesData.value?.data?.feedback_categories)
  const suggestCategoryId = computed(
    () => feedbackCategories.value?.find((cat) => cat.name === 'Предложи свой мерч')?.id,
  )

  const formRef = useTemplateRef('formRef')
  const uploadRef = useTemplateRef('uploadRef')
  const showCancelDialog = ref(false)

  const { submit, handleChange, handleBeforeUpload, resetForm } = useFeedbackForm({
    formRef,
    uploadRef,
    resolveCategoryId: () => suggestCategoryId.value,
    buildResetValues: () => ({
      subject: null,
      email: null,
      text: null,
      attachments: [],
    }),
    messages: {
      success: 'shop-suggest_send-success',
      error: 'shop-suggest_send-error',
      oversize: 'max_img_size_text_5mb',
    },
  })

  const handleSubmit = () => {
    formRef.value?.onSubmit()
  }
</script>

<template>
  <div class="wide-responsive relative flex h-full flex-col flex-nowrap! overflow-hidden">
    <PageDecorationImage />

    <ConfirmCancelModal
      v-model:show="showCancelDialog"
      message="shop-suggest_cancel-message"
      @confirm="resetForm"
    />

    <ShopSuggestMerchPageHeader class="border-b p-6 pt-8" />

    <div class="flex grow flex-col flex-nowrap!">
      <div class="relative p-6">
        <UiForm
          ref="formRef"
          :submit="submit"
          :initial-values="DEFAULT_FORM_STATE"
          class="flex flex-col flex-nowrap! gap-5 [&_label]:font-semibold"
        >
          <div class="flex flex-nowrap! gap-5">
            <UiInputString
              :label="$t('feedback_edit_subject')"
              name="subject"
              :rules="{
                required: true,
                min: SHOP_SUGGEST_SUBJECT_LENGTH.MIN,
              }"
              :maxlength="SHOP_SUGGEST_SUBJECT_LENGTH.MAX"
              show-count
            />
            <UiInputString
              :label="$t('feedback_edit_email')"
              name="email"
              rules="required|email"
            />
          </div>
          <UiInputString
            :label="$t('feedback_edit_message')"
            name="text"
            :input-props="{
              class: 'min-h-32.5',
            }"
            :rules="{
              required: true,
              min: SHOP_SUGGEST_TEXT_LENGTH.MIN,
            }"
            :maxlength="SHOP_SUGGEST_TEXT_LENGTH.MAX"
            show-count
            type="textarea"
          />
          <UiInputFile
            v-slot="{ deleteFileByIndex }"
            :label="$t('base_photos')"
            name="attachments"
          >
            <NUpload
              ref="uploadRef"
              :default-upload="false"
              :show-file-list="true"
              list-type="image-card"
              accept="image/*"
              :max="ATTACHMENTS_MAX_COUNT"
              class="group [&_.n-upload-file]:size-20! [&_.n-upload-file-list]:flex! [&_.n-upload-file-list]:gap-4! [&_.n-upload-trigger]:size-20!"
              @before-upload="handleBeforeUpload"
              @change="handleChange"
              @remove="($event) => deleteFileByIndex($event.index)"
            >
              <div class="body-xs group-hover:text-body! text-subtitle! p-2">
                {{ $t('base_choose_file') }}
              </div>
            </NUpload>
          </UiInputFile>
        </UiForm>

        <div class="bg-surface-bg wide-responsive fixed bottom-0 z-2 flex w-full shrink-0 gap-1.5 border-t px-6 py-3">
          <NButton
            class="w-40.75!"
            type="primary"
            attr-type="button"
            strong
            data-testid="shop-suggest-submit"
            :disabled="formRef?.loading || !formRef?.meta.valid"
            :loading="formRef?.loading"
            @click="handleSubmit"
          >
            {{ $t('base_send') }}
          </NButton>
          <NButton
            secondary
            strong
            attr-type="button"
            :disabled="formRef?.loading || !formRef?.meta.dirty"
            @click="showCancelDialog = true"
          >
            {{ $t('base_cancel') }}
          </NButton>
        </div>
      </div>
    </div>
  </div>
</template>
